import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import TemplateConfigModal from './TemplateConfigModal';

const SettingsPage = () => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);

  useEffect(() => {
    loadCurrentConfig();
  }, []);

  const loadCurrentConfig = () => {
    const saved = localStorage.getItem('templateConfig');
    if (saved) {
      try {
        setCurrentConfig(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse template config:', e);
        setCurrentConfig(null);
      }
    } else {
      setCurrentConfig(null);
    }
  };

  const handleModalClose = () => {
    setShowConfigModal(false);
    loadCurrentConfig(); // Reload config after modal closes
  };

  const getConfigStatus = () => {
    if (!currentConfig) {
      return {
        status: 'Not Configured',
        color: '#dc3545',
        icon: '⚠️'
      };
    }

    if (currentConfig.login_template_id &&
        currentConfig.pep_template_id &&
        currentConfig.age_template_id) {
      return {
        status: 'Configured',
        color: '#28a745',
        icon: '✓'
      };
    }

    return {
      status: 'Incomplete',
      color: '#ffc107',
      icon: '⚠️'
    };
  };

  const configStatus = getConfigStatus();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Header />

      <main style={{ padding: '60px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '30px' }}>
            <Link to="/" style={{
              color: '#007bff',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              ← Back to Home
            </Link>
          </div>

          <h1 style={{ marginBottom: '10px', color: '#333' }}>Settings</h1>
          <p style={{ color: '#666', marginBottom: '40px' }}>
            Manage your Floors application configuration
          </p>

          {/* Template Configuration Card */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#333' }}>
                  Credential Template Configuration
                </h2>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  Configure which credential templates are used for user registration and login
                </p>
              </div>
              <div style={{
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: configStatus.color + '15',
                color: configStatus.color,
                fontWeight: '500',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>{configStatus.icon}</span>
                <span>{configStatus.status}</span>
              </div>
            </div>

            {currentConfig ? (
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Current Configuration
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      Login Credential
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
                      Template ID: {currentConfig.login_template_id}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      PEP Credential
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
                      Template ID: {currentConfig.pep_template_id}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      Age Credential
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
                      Template ID: {currentConfig.age_template_id}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <div>
                  <strong style={{ color: '#856404' }}>No configuration found</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#856404', fontSize: '14px' }}>
                    You need to configure template IDs before users can register or login.
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowConfigModal(true)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              {currentConfig ? 'Update Configuration' : 'Configure Templates'}
            </button>
          </div>

          {/* Information Card */}
          <div className="card">
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#333' }}>
              About Template Configuration
            </h2>
            <div style={{ color: '#666', lineHeight: '1.8' }}>
              <p>
                The Floors application uses credential templates from your Juno dashboard to issue
                verifiable credentials to users. You need to configure three template IDs:
              </p>
              <ul style={{ marginTop: '12px', paddingLeft: '24px' }}>
                <li><strong>Login Credential:</strong> Used for wallet-based authentication</li>
                <li><strong>PEP Credential:</strong> Issued after Politically Exposed Person verification</li>
                <li><strong>Age Credential:</strong> Contains age verification (over 16/18/21) claims</li>
              </ul>
              <p style={{ marginTop: '16px' }}>
                To find your template IDs, log in to your Juno dashboard and navigate to the
                Credentials section where you can view all your templates.
              </p>
            </div>
          </div>
        </div>
      </main>

      <TemplateConfigModal
        show={showConfigModal}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default SettingsPage;
